/**
 * GPS Data Provider Type Definitions
 * Defines interfaces and types for GPS data processing and distribution
 * 
 * Based on official GPS WebSocket protocol:
 * - Data encoding: JSON
 * - Request format: wsClient.send(JSON.stringify(payload))
 * - Coordinate format: "lat_lng" (underscore separator)
 * - SequenceId: UNIX timestamp milliseconds (recommended)
 */

import type { DeviceGpsInfo } from '../gps-websocket/types';

/**
 * GPS Data Provider configuration interface
 */
export interface GpsDataProviderConfig {
  // 데이터 처리 설정
  updateThreshold: {
    position: number;      // 위치 변경 임계값 (도)
    speed: number;         // 속도 변경 임계값 (km/h)
    interval: number;      // 최소 업데이트 간격 (ms)
  };
  
  // 캐싱 설정
  enableCaching: boolean;
  cacheSize: number;       // 히스토리 보관 개수
  
  // 필터링 설정
  autoFilter: {
    excludeInactive: boolean;
    excludeInvalidCoords: boolean;
    excludeStatic: boolean;  // 움직이지 않는 디바이스
    minSpeedThreshold: number; // 정적 디바이스 판단 속도 (km/h)
  };
  
  // 성능 설정
  maxDevicesPerUpdate: number;  // 한 번에 처리할 최대 디바이스 수
  enableBatching: boolean;      // 배치 처리 활성화
  batchInterval: number;        // 배치 처리 간격 (ms)
}

/**
 * GPS Data request configuration for different actions
 */
export interface GpsDataRequestConfig {
  // Common parameters
  action: string;
  userEmail?: string;
  
  // Zone-based parameters
  sw?: string;                    // Southwest coordinate (lat_lng format)
  ne?: string;                    // Northeast coordinate (lat_lng format)
  
  // PSN-based parameters
  psn?: string;                   // Single device PSN
  psnList?: string[];             // Multiple device PSNs
  
  // Group-based parameters
  groupIdList?: string[];         // Group ID list
  
  // Additional options
  useCache?: boolean;             // Whether to use cache
  cacheTimeout?: number;          // Cache timeout in ms
}

/**
 * GPS Data Provider action types
 */
export type GpsDataProviderAction = 
  | 'gpsZoneNoAuth'
  | 'gpsZoneMyDevices'
  | 'gpsZoneAllDevices'
  | 'gpsZoneGroupMyDevices'
  | 'gpsZoneGroupAllDevices'
  | 'gpsPsnsMyDevices'
  | 'gpsPsnsSpecificDevice'
  | 'gpsPsnsMultiLiveView';

/**
 * GPS Data statistics interface
 */
export interface GpsDataStats {
  // 연결 통계
  isConnected: boolean;
  lastUpdateTime: Date | null;
  updateCount: number;
  updatesPerMinute: number;
  averageUpdateInterval: number;
  
  // 데이터 통계
  totalDevices: number;
  activeDevices: number;
  inactiveDevices: number;
  videoSharingDevices: number;
  
  // 성능 통계
  processedUpdates: number;
  skippedUpdates: number;
  optimizationRatio: number;     // 최적화로 스킵된 비율
  
  // 에러 통계
  errorCount: number;
  lastError: string | null;
}

/**
 * GPS Data Manager callback types
 */
export interface GlobalGpsStats {
  totalProviders: number;
  totalDevices: number;
  totalUpdatesPerMinute: number;
  memoryUsage: {
    providers: number;
    cachedData: number;
    callbacks: number;
  };
  performance: {
    averageProcessingTime: number;
    peakProcessingTime: number;
    totalProcessedUpdates: number;
  };
}

export type GpsManagerDataCallback = (allData: Map<string, DeviceGpsInfo[]>) => void;
export type GpsManagerStatsCallback = (stats: GlobalGpsStats) => void;
// Forward declaration for GpsDataProvider
export interface GpsDataProvider {
  getState(): GpsDataProviderState;
  destroy(): void;
  clearAllCallbacks(): void;
}

export type GpsManagerProviderCallback = (providerId: string, provider?: GpsDataProvider) => void;

/**
 * GPS Data Provider state interface
 */
export interface GpsDataProviderState {
  // 연결 상태
  isConnected: boolean;
  isInitialized: boolean;
  
  // 데이터 상태
  currentDevices: DeviceGpsInfo[];
  deviceCount: number;
  
  // 통계
  stats: GpsDataStats;
  
  // 콜백 정보
  callbackCount: number;
}

/**
 * Data processing result interface
 */
export interface DataProcessingResult {
  processed: DeviceGpsInfo[];
  skipped: DeviceGpsInfo[];
  added: DeviceGpsInfo[];
  updated: DeviceGpsInfo[];
  removed: string[];  // PSN list
  hasSignificantChanges: boolean;
}

/**
 * Default configuration values
 */
export const DEFAULT_GPS_PROVIDER_CONFIG: Partial<GpsDataProviderConfig> = {
  updateThreshold: {
    position: 0.0001,  // ~10m
    speed: 1,          // 1km/h
    interval: 1000     // 1초
  },
  enableCaching: true,
  cacheSize: 100,
  autoFilter: {
    excludeInactive: false,
    excludeInvalidCoords: true,
    excludeStatic: false,
    minSpeedThreshold: 0.5  // 0.5km/h 이하는 정적으로 간주
  },
  maxDevicesPerUpdate: 50,
  enableBatching: true,
  batchInterval: 2000  // 2초
};